import React, { forwardRef, useContext, useMemo } from 'react';
import proptypes from 'prop-types';
import { extend, QueryContext } from '@thd-nucleus/data-sources';
import { useBreakpoint } from '@one-thd/sui-atomic-components';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import { Row } from '@thd-olt-component-react/core-ui';
import { SponsoredInGridBanner } from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { CategoryCardsDesktop, CategoryCardsMobile } from '@thd-olt-component-react/category-cards';
import { DiscoveryZonesWrapper } from '@thd-olt-component-react/thd-recs-containers';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { createGridWithPod, ProductResultsContext } from '@thd-olt-component-react/product-results';
import { CustomProductPodResponsive } from './CustomProductPodResponsive';

const {
  string, func, bool, number, arrayOf, shape
} = proptypes;

const DefaultResultsGrid = createGridWithPod(CustomProductPodResponsive);

const DefaultGrid = forwardRef(({
  pageType, defaultVariables,
  handleProductClick, productPodHoverDelay, onCompare, onSelectedForCompare,
  selectedForCompare, isInStoreFilterON, samplesLoading, hasSameDayFilter,
  impProductPodData, nValue,
  additionalSearchParams,
  keyword,
  storeId,
  ssrProductRequestCount,
  dynamicHydrationProductRequestCount,
  isMobileOrConsumerApp,
  isInGridBannerBottom
}, ref) => {
  const { data, loading, error } = useContext(ProductResultsContext);
  const { channel } = useContext(ExperienceContext);
  const matchesScreen = useBreakpoint('sm', { current: true });
  const isDesktop = channel === 'desktop';
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const thdCustomer = useThdCustomer();
  const sponsoredCarouselConfig = useConfigService('fs-prop:tnt-carouselType');
  const isInGridBannerEnabled = useConfigService('fs-prop:isInGridBannerEnabled') || false;
  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  // Combine these flags into a single condition we can check more easily.
  const isDiscoveryZonesEligible = (isBrowse && isDiscoveryZonesEnabled) || (isSearch && isDiscoveryZonesSearchEnabled);
  const { isClientResolved } = useContext(QueryContext);
  const isClientSearchModelResolved = isClientResolved({ queryName: 'searchModel' });

  const shouldShowSponsoredCarousel = useMemo(() => {
    return (sponsoredCarouselConfig === 'standardDZ'
      || sponsoredCarouselConfig === 'longtailDZ'
      || sponsoredCarouselConfig === 'fourStarDZ');
  }, [sponsoredCarouselConfig]);
  const shouldShowDiscoveryZones = (isBrowse && !shouldShowSponsoredCarousel) || (isSearch && isDiscoveryZonesEligible);
  const shouldShowRetailMediaCarousel = isBrowse && shouldShowSponsoredCarousel;
  // this is needed for sponsored in grid
  const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray.length > 0;
  const { metadata } = data?.searchModel || {};
  const { contentType } = metadata || {};
  const isCategory = contentType === 'categorypage';

  const slots = useMemo(() => {
    const slotsList = [
      {
        position: {
          xl: 3, lg: 3, md: 6, sm: 6, unprefixed: 6
        },
        component: isDesktop ? (
          <CategoryCardsDesktop
            data={data}
            error={error}
            loading={loading}
            storeId={storeId}
            keyword={keyword}
            navParam={nValue}
          />
        ) : (
          <CategoryCardsMobile
            data={data}
            error={error}
            loading={loading}
            storeId={storeId}
            keyword={keyword}
            navParam={nValue}
          />
        )
      }
    ];

    // add DZ
    if (isDiscoveryZonesEligible) {
      slotsList.push({
        position: {
          xl: 2, lg: 2, md: 2, sm: 2, unprefixed: 4
        },
        component: (
          <Row key="discovery-zone-1" data-testid="discovery-zone-1">
            <DiscoveryZonesWrapper
              data={data}
              loading={loading}
              nValue={nValue}
              schemaName="discoveryZones"
              storeId={storeId}
              zoneId="1"
              errorBoundary
              hydrator={{
                className: 'sui-bg-primary sui-grid sui-ml-3 sui-mb-4'
                + ' sui-w-full',
                delay: 1500,
                scrollBuffer: 500,
                id: 'discovery-zones-1'
              }}
              dynamic={{
                pageType
              }}
            />
          </Row>
        )
      });
    }

    if (shouldShowDiscoveryZones) {
      slotsList.push(
        {
          position: {
            xl: 4, lg: 4, md: 4, sm: 4, unprefixed: 8
          },
          component: (
            <Row key="discovery-zone-2" data-testid="discovery-zone-2">
              <DiscoveryZonesWrapper
                data={data}
                loading={loading}
                nValue={nValue}
                schemaName="discoveryZones"
                storeId={storeId}
                zoneId="2"
                errorBoundary
                hydrator={{
                  className: 'sui-bg-primary sui-grid sui-ml-3 sui-mb-4'
                + ' sui-w-full',
                  delay: 1500,
                  scrollBuffer: 500,
                  id: 'discovery-zones-2'
                }}
                dynamic={{
                  pageType
                }}
              />
            </Row>
          )
        });
    }
    // add sponsored carousel
    if (shouldShowRetailMediaCarousel) {
      slotsList.push(
        {
          position: {
            xl: 4, lg: 4, md: 4, sm: 4, unprefixed: 8
          },
          component: (
            <Row className="grid sui-pb-4 sui-pl-2" data-testid="SponsoredCarouselWrapper">
              <RetailMediaCarousel
                pageContext={{
                  schema: 'discovery_zone',
                  isDiscoveryZone: true
                }}
                showSponsoredCarousel
                plaLocation="sponsoredCarouselBrowsePageDiscoveryZone"
                nValue={nValue}
                tntCarouselType={sponsoredCarouselConfig}
                dynamic={{
                  pageType
                }}
              />
            </Row>
          )
        });
    }

    if (isInGridBannerEnabled && !isInGridBannerBottom && isClientSearchModelResolved) {
      let position = { unprefixed: 3, sm: 3, md: 3 };
      const productsLength = data?.searchModel?.products.length;
      if (['xl', '2xl'].includes(matchesScreen.current) && hasCategoryCard
      && productsLength >= 24) {
        position = { ...position, xl: 6, lg: 6 };
      } else if (['xl', '2xl'].includes(matchesScreen.current) && !hasCategoryCard
      && productsLength >= 12) {
        position = { ...position, xl: 3, lg: 3 };
      }
      if (matchesScreen.current === 'lg' && productsLength >= 18 && hasCategoryCard) {
        position = { ...position, xl: 6, lg: 6 };
      } else if (matchesScreen.current === 'lg' && productsLength >= 9 && !hasCategoryCard) {
        position = { ...position, xl: 3, lg: 3 };
      }
      slotsList.push(
        {
          position,
          component: (
            <Row className="sui-mb-4 sui-ml-2" data-testid="SponsoredInGridBanner">
              <ErrorBoundary name="sponsored-ingrid-nucleus">
                <SponsoredInGridBanner
                  browserId={thdCustomer?.mcvisID || ''}
                  pageContext={{
                    label: 'browse-search',
                    data,
                    keyword,
                    isCategory,
                    isBrowse,
                    isSearch
                  }}
                />
              </ErrorBoundary>
            </Row>
          )
        });
    }
    return slotsList;
  }, [isDiscoveryZonesEnabled, isDiscoveryZonesSearchEnabled,
    shouldShowSponsoredCarousel, data, matchesScreen.current]);

  const renderPod = (product, index) => {
    return (
      <CustomProductPodResponsive
        itemId={product.itemId}
        storeId={storeId}
        position={index}
        onClick={handleProductClick}
        hoverDelay={productPodHoverDelay}
        onCompare={onCompare}
        onSelectedForCompare={onSelectedForCompare}
        selectedForCompare={selectedForCompare}
        hasInStoreFilter={isInStoreFilterON}
        samplesLoading={samplesLoading}
        impProductPodData={impProductPodData}
        hasSameDayFilter={hasSameDayFilter}
        isMobileOrConsumerApp={isMobileOrConsumerApp}
      />
    );
  };

  return (
    <DefaultResultsGrid
      additionalSearchParams={additionalSearchParams}
      keyword={keyword}
      storeId={storeId}
      pageType={pageType}
      ref={ref}
      defaultVariables={defaultVariables}
      slots={slots}
      podFunction={renderPod}
      ssrProductRequestCount={ssrProductRequestCount}
      dynamicHydrationProductRequestCount={dynamicHydrationProductRequestCount}
    />
  );
});

DefaultGrid.displayName = 'DefaultGrid';
DefaultGrid.dataModel = extend({},
  DefaultResultsGrid,
  DiscoveryZonesWrapper,
  CategoryCardsDesktop, CategoryCardsMobile
);

DefaultGrid.propTypes = {
  nValue: string,
  keyword: string,
  storeId: string.isRequired,
  defaultVariables: shape({}).isRequired,
  additionalSearchParams: shape({}),
  pageType: string,
  handleProductClick: func,
  productPodHoverDelay: number,
  onCompare: func,
  onSelectedForCompare: func,
  selectedForCompare: arrayOf(string),
  isInStoreFilterON: bool,
  samplesLoading: bool,
  hasSameDayFilter: bool,
  impProductPodData: shape({}),
  ssrProductRequestCount: number,
  dynamicHydrationProductRequestCount: number,
  isMobileOrConsumerApp: bool,
  isInGridBannerBottom: bool
};

DefaultGrid.defaultProps = {
  nValue: '',
  keyword: null,
  pageType: null,
  additionalSearchParams: null,
  handleProductClick: undefined,
  productPodHoverDelay: 500,
  onCompare: null,
  onSelectedForCompare: null,
  selectedForCompare: null,
  isInStoreFilterON: false,
  samplesLoading: false,
  hasSameDayFilter: false,
  impProductPodData: null,
  ssrProductRequestCount: 12,
  dynamicHydrationProductRequestCount: 12,
  isMobileOrConsumerApp: false,
  isInGridBannerBottom: false
};

export { DefaultGrid };
